import { LogIn, RefreshCw, WifiOff } from "lucide-react";
import { BrandMark, Wordmark } from "./brand";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Spinner } from "./ui/spinner";

export function SignIn({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center gap-6 bg-muted/40 p-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,color-mix(in_oklch,var(--brand)_14%,transparent),transparent_60%)]"
      />
      <div className="relative flex flex-col items-center gap-4">
        <BrandMark className="size-16 rounded-2xl shadow-sm" />
        <Wordmark className="h-4" />
      </div>
      <Card className="relative w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in with your Leaselab account to open the factory.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" size="lg" onClick={onSignIn}>
            <LogIn /> Continue with Microsoft
          </Button>
        </CardContent>
      </Card>
      <p className="relative text-xs text-muted-foreground">Access is limited to Leaselab staff.</p>
    </div>
  );
}

/** Shown instead of the app when the API cannot be reached, for example an installed app opened offline. */
export function Unreachable({ retrying, onRetry }: { retrying: boolean; onRetry: () => void }) {
  const offline = !navigator.onLine;
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-5 p-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center">
      {offline ? (
        <WifiOff className="size-10 text-muted-foreground" />
      ) : (
        <BrandMark className="size-14 rounded-2xl shadow-sm" />
      )}
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">{offline ? "You're offline" : "Can't reach Skynet"}</h1>
        <p className="max-w-xs text-sm text-muted-foreground">
          {offline
            ? "Skynet reconnects on its own once you're back online."
            : "The server didn't answer. It may be restarting; try again in a moment."}
        </p>
      </div>
      <Button variant="outline" onClick={onRetry} disabled={retrying}>
        {retrying ? <Spinner /> : <RefreshCw />}
        Try again
      </Button>
    </div>
  );
}

export function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
      <Spinner className="size-6" />
      {label}
    </div>
  );
}
